import {
  MessageSquare,
  CheckCircle,
  User,
  Paperclip,
  FileEdit,
  Ticket,
  GitMerge,
  type LucideIcon,
} from 'lucide-react';

export type ActivityType = 'comment' | 'status_change' | 'assignment' | 'attachment' | 'update' | 'create' | 'merged';

export interface ActivityIconConfig {
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

export const activityIcons: Record<ActivityType, ActivityIconConfig> = {
  comment: {
    icon: MessageSquare,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
  },
  merged: {
    icon: GitMerge,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
  },
  status_change: {
    icon: CheckCircle,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  assignment: {
    icon: User,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
  },
  attachment: {
    icon: Paperclip,
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30',
  },
  update: {
    icon: FileEdit,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
  },
  create: {
    icon: Ticket,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
  },
};

export function mapActionToActivityType(action: string): ActivityType {
  const normalized = action.toLowerCase().replace(/\s+/g, '');

  // Create actions
  if (['create', 'created', 'ticketcreated'].includes(normalized)) {
    return 'create';
  }
  // Comment actions
  if (['comment', 'commented', 'commentadded', 'customercommented', 'internalnote', 'internalcommentadded'].includes(normalized)) {
    return 'comment';
  }
  // Assignment actions
  if (['assign', 'assigned', 'reassigned', 'ticketassigned', 'ticketunassigned', 'ticketreassigned'].includes(normalized)) {
    return 'assignment';
  }
  // Attachment actions
  if ([
    'attachment', 'attachmentadded', 'attachmentdeleted',
    'commentattachmentadded', 'internalcommentattachmentadded', 'internalcommentattachmentdeleted'
  ].includes(normalized)) {
    return 'attachment';
  }
  // Merge actions
  if (['ticketmerged', 'merged', 'mergedinto', 'mergedfrom'].includes(normalized)) {
    return 'merged';
  }
  // Status change actions
  if ([
    'statuschanged', 'resolved', 'closed', 'reopened',
    'ticketresolved', 'ticketclosed', 'ticketinprogress', 'ticketonhold', 'ticketpending', 'ticketreopened'
  ].includes(normalized)) {
    return 'status_change';
  }
  // Update actions (field changes)
  if ([
    'subjectchanged', 'descriptionupdated',
    'priorityescalated', 'priorityreduced',
    'categoryassigned', 'categoryremoved',
    'duedateset', 'duedateremoved'
  ].includes(normalized)) {
    return 'update';
  }
  // Default for unknown actions
  return 'update';
}

export function getActivityIcon(action: string): ActivityIconConfig {
  const type = mapActionToActivityType(action);
  return activityIcons[type];
}

export function getActivityIconByType(type: string): ActivityIconConfig {
  if (type in activityIcons) {
    return activityIcons[type as ActivityType];
  }
  return activityIcons.update;
}
